import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Briefcase, DollarSign, ArrowRight } from 'lucide-react';
import { API } from '../App';
import Autoplay from "embla-carousel-autoplay";

const FeaturedJobsCarousel = ({ onJobClick }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const autoplayPlugin = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false })
  );

  useEffect(() => {
    fetchFeaturedJobs();
  }, []);

  const fetchFeaturedJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/featured?limit=5`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching featured jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSpanishLevelBadge = (level) => {
    const variants = {
      native: { color: 'bg-emerald-700 text-white', label: 'Nativo' },
      advanced: { color: 'bg-emerald-600 text-white', label: 'Avanzado' },
      intermediate: { color: 'bg-teal-600 text-white', label: 'Intermedio' },
      basic: { color: 'bg-teal-500 text-white', label: 'Básico' },
      'not-required': { color: 'bg-slate-200 text-slate-700', label: 'No requerido' }
    };
    const variant = variants[level] || variants['not-required'];
    return <Badge className={`${variant.color} font-medium`}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  if (jobs.length === 0) return null;

  return (
    <div className="w-full max-w-5xl mx-auto px-4">
      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        plugins={[autoplayPlugin.current]}
        className="w-full"
      >
        <CarouselContent>
          {jobs.map((job) => (
            <CarouselItem key={job.id}>
              <Card className="border-2 border-blue-900 shadow-xl hover:shadow-2xl transition-all">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">Destacado</Badge>
                        {getSpanishLevelBadge(job.spanish_level)}
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{job.title}</h3>
                      <p className="text-xl font-semibold text-blue-900 mb-4">{job.company}</p>
                    </div>
                    <Briefcase className="w-12 h-12 text-blue-900 opacity-20" />
                  </div>

                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex items-center text-slate-700">
                      <MapPin className="w-5 h-5 mr-2 text-blue-900" />
                      <span className="font-medium">{job.location}</span>
                    </div>
                    {job.salary && (
                      <div className="flex items-center text-slate-700">
                        <DollarSign className="w-5 h-5 mr-2 text-emerald-700" />
                        <span className="font-medium">{job.salary}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-slate-600 mb-6 line-clamp-2 leading-relaxed">{job.description}</p>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => onJobClick(job.id)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white h-12"
                      data-testid={`featured-job-${job.id}`}
                    >
                      Ver Oferta
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="hidden md:flex" />
        <CarouselNext className="hidden md:flex" />
      </Carousel>
    </div>
  );
};

export default FeaturedJobsCarousel;
